import { formatCurrency, formatDuration } from '@/lib/format'

/**
 * A small, deterministic stand-in for the Verdane AI used by the public demo.
 * It answers from one sample household, so every number is consistent.
 */

export const HOUSEHOLD = {
    name: 'Maya',
    age: 34,
    role: 'Product designer',
    city: 'Denver',
    income: 6200,
    autoSave: 600,
    retirementMonthly: 750,
    fixed: [
        { label: 'Rent', amount: 1850 },
        { label: 'Car payment', amount: 340 },
        { label: 'Utilities and internet', amount: 210 },
        { label: 'Insurance', amount: 157 },
        { label: 'Subscriptions', amount: 92 },
        { label: 'Phone', amount: 65 }
    ],
    variable: [
        { id: 'groceries', label: 'Groceries', amount: 540, typical: 510, keywords: ['grocer', 'supermarket', 'food shop'] },
        { id: 'dining', label: 'Dining out', amount: 320, typical: 262, keywords: ['dining', 'restaurant', 'eating out', 'takeout', 'take out', 'coffee', 'lunch'] },
        { id: 'shopping', label: 'Shopping', amount: 280, typical: 300, keywords: ['shopping', 'clothes', 'online orders'] },
        { id: 'fun', label: 'Fun and going out', amount: 196, typical: 180, keywords: ['fun', 'entertainment', 'going out', 'bars', 'concert'] },
        { id: 'transport', label: 'Transport', amount: 190, typical: 205, keywords: ['transport', 'gas', 'fuel', 'uber', 'lyft', 'transit', 'parking'] },
        { id: 'other', label: 'Everything else', amount: 120, typical: 125, keywords: [] as Array<string> }
    ],
    balances: [
        { label: 'Checking', amount: 4820 },
        { label: 'Savings', amount: 12400 },
        { label: 'Investments', amount: 28900 },
        { label: 'Retirement', amount: 41200 },
        { label: 'Credit card', amount: -1240 },
        { label: 'Car loan', amount: -9600 }
    ],
    subscriptions: [
        { label: 'Gym membership', amount: 49, note: 'no visits in seven weeks', used: false },
        { label: 'Meditation app', amount: 12.99, note: 'not opened in three months', used: false },
        { label: 'Streaming', amount: 15.49, note: 'used most evenings', used: true },
        { label: 'Music', amount: 11.99, note: 'used daily', used: true },
        { label: 'Cloud storage', amount: 2.99, note: 'in use', used: true }
    ],
    trip: { label: 'Trip to Portugal', saved: 1800, target: 4500, monthly: 250 }
}

const FIXED_TOTAL = HOUSEHOLD.fixed.reduce((sum, item) => sum + item.amount, 0)
const VARIABLE_TOTAL = HOUSEHOLD.variable.reduce((sum, item) => sum + item.amount, 0)
const LEFTOVER = HOUSEHOLD.income - FIXED_TOTAL - VARIABLE_TOTAL - HOUSEHOLD.autoSave
const SAVINGS = 12400
const CHECKING = 4820
const ESSENTIALS = FIXED_TOTAL + 540 + 190

export const MONTHLY_FLOW = [
    { label: 'Take-home pay', amount: HOUSEHOLD.income },
    { label: 'Fixed bills', amount: -FIXED_TOTAL },
    { label: 'Everyday spending', amount: -VARIABLE_TOTAL },
    { label: 'Automatic savings', amount: -HOUSEHOLD.autoSave },
    { label: 'Free this month', amount: LEFTOVER }
]

export interface DemoAnswer {
    paragraphs: Array<string>
    followUps: Array<string>
}

const money = (value: number, decimals = 0): string => formatCurrency(value, { decimals })

const has = (text: string, words: Array<string>): boolean => words.some(word => text.includes(word))

export const parseAmount = (question: string): number | null => {
    const dollar = question.match(/\$\s?(\d{1,3}(?:,\d{3})+|\d+(?:\.\d+)?)(?:\s?(k|thousand|million|m)\b)?/i)
    const suffixed = question.match(/\b(\d{1,3}(?:,\d{3})+|\d+(?:\.\d+)?)\s?(k|thousand|grand)\b/i)
    const match = dollar ?? suffixed

    if (!match) {
        const bare = question.match(/\b(\d{2,7})\b(?!\s*(?:%|percent|years?|yrs?|months?|days?|weeks?|times))/i)

        if (!bare) return null

        const value = Number(bare[1])

        return value >= 50 && !(value >= 1900 && value <= 2100) ? value : null
    }

    let value = Number(match[1].replace(/,/g, ''))
    const unit = match[2]?.toLowerCase()

    if (unit === 'k' || unit === 'thousand' || unit === 'grand') value *= 1000
    if (unit === 'm' || unit === 'million') value *= 1000000

    return Number.isFinite(value) && value > 0 ? value : null
}

const futureValue = (start: number, monthly: number, annualRate: number, months: number): number => {
    const rate = annualRate / 12

    if (rate === 0) return start + monthly * months

    const growth = Math.pow(1 + rate, months)

    return start * growth + monthly * ((growth - 1) / rate)
}

const afford = (amount: number, question: string): DemoAnswer => {
    const investing = question.includes('invest')
    const remaining = LEFTOVER - amount

    if (amount <= LEFTOVER * 0.6) {
        return {
            paragraphs: [
                'Yes, with room to spare.',
                `Once rent, bills and your ${money(HOUSEHOLD.autoSave)} of automatic saving are covered, ${money(LEFTOVER)} is free this month. ${investing ? 'Investing' : 'Spending'} ${money(amount)} would still leave ${money(remaining)} in reserve.`,
                investing
                    ? `My suggestion: ${money(Math.round(amount * 0.7))} to your index fund and ${money(Math.round(amount * 0.3))} to your emergency pot, which covers 3.6 months right now against a goal of six.`
                    : 'I can also hold it back from your next paycheck, so your checking account never feels the dip.'
            ],
            followUps: ['What size emergency fund do I need?', 'Where does my money actually go?']
        }
    }

    if (amount <= LEFTOVER) {
        return {
            paragraphs: [
                'Yes, though it would be close.',
                `That takes most of this month's free ${money(LEFTOVER)}, with ${money(remaining)} left over for surprises.`,
                `Spread it over two months instead, ${money(Math.ceil(amount / 2))} now and the remainder next month, and your buffer stays healthier.`
            ],
            followUps: ['Am I paying for subscriptions I never use?', 'Can you draw up a budget for me?']
        }
    }

    if (amount <= LEFTOVER + CHECKING - 1500) {
        const months = Math.ceil(amount / (LEFTOVER * 0.7))

        return {
            paragraphs: [
                'This month alone will not cover it.',
                `With ${money(LEFTOVER)} free this month, paying ${money(amount)} would mean drawing ${money(amount - LEFTOVER)} out of your checking buffer.`,
                `A gentler path: set aside ${money(Math.ceil(amount / months))} a month and you will have it in ${formatDuration(months)}, with your savings left alone.`
            ],
            followUps: [`When could I have ${money(amount)} saved?`, 'Where does my money actually go?']
        }
    }

    const monthly = Math.round(LEFTOVER * 0.7)
    const months = Math.ceil(amount / monthly)

    return {
        paragraphs: [
            'That is a stretch at the moment.',
            `${money(amount)} is more than this month and your checking buffer can carry without strain.`,
            `Putting away ${money(monthly)} a month would get you there in roughly ${formatDuration(months)}. I can open a pot just for this and keep an eye on its progress.`
        ],
        followUps: ['Should I clear my card before investing?', 'Can you draw up a budget for me?']
    }
}

const timeToSave = (target: number): DemoAnswer => {
    const months = Math.ceil(target / HOUSEHOLD.autoSave)
    const faster = Math.ceil(target / (HOUSEHOLD.autoSave + 200))

    return {
        paragraphs: [
            `Saving ${money(HOUSEHOLD.autoSave)} a month as you do now, you reach ${money(target)} in about ${formatDuration(months)}.`,
            `Move another ${money(200)} a month across from your free cash and that shortens to ${formatDuration(faster)}.`,
            'Shall I give it a pot of its own, kept apart from your emergency fund?'
        ],
        followUps: ['Is there room to invest $500 this month?', 'What size emergency fund do I need?']
    }
}

const categoryAnswer = (category: typeof HOUSEHOLD.variable[number]): DemoAnswer => {
    const difference = category.amount - category.typical
    const percent = Math.round((Math.abs(difference) / category.typical) * 100)
    const comparison = difference > 0
        ? `${percent}% above your typical ${money(category.typical)}`
        : `${percent}% below your typical ${money(category.typical)}`

    const tips: Record<string, string> = {
        groceries: 'Two large weekend shops explain most of the rise. Planning a week of meals ahead tends to cut about 10%.',
        dining: 'Weekday lunches make up most of it. Packing lunch twice a week would keep about $90 a month in your pocket.',
        shopping: 'Comfortably below normal, which leaves $20 you could move into savings.',
        fun: 'Slightly higher than usual, mainly from two concerts, and still well within budget.',
        transport: 'A touch below normal, since you took fewer rideshares this month.',
        other: 'Holding steady, as it usually does.'
    }

    return {
        paragraphs: [
            `This month ${category.label.toLowerCase()} came to ${money(category.amount)}, ${comparison}.`,
            tips[category.id] ?? ''
        ].filter(Boolean),
        followUps: ['Where does my money actually go?', 'Can you draw up a budget for me?']
    }
}

const ANSWERS = {
    greeting: (): DemoAnswer => ({
        paragraphs: [
            'Hello, this is the Verdane AI.',
            `In this demo I am looking after ${HOUSEHOLD.name}, ${HOUSEHOLD.age}, a ${HOUSEHOLD.role.toLowerCase()} in ${HOUSEHOLD.city}. Ask about her spending, savings, debts or plans for the future.`
        ],
        followUps: ['Is there room to invest $500 this month?', 'Where does my money actually go?']
    }),
    thanks: (): DemoAnswer => ({
        paragraphs: ['Glad to help. Most real progress comes from small, regular check-ins like this one.'],
        followUps: ['Will I have enough when I retire?', 'Am I paying for subscriptions I never use?']
    }),
    capabilities: (): DemoAnswer => ({
        paragraphs: [
            'Ask me about your money in everyday words. A few things I handle well:',
            '• Whether a purchase fits comfortably this month',
            '• Where your money went, and how that compares with normal',
            '• When a goal is within reach, and how to bring it closer',
            '• Which debt to clear first, and when investing makes more sense',
            'When things get complicated, I can pass you to one of our human advisors.'
        ],
        followUps: ['Where does my money actually go?', 'Should I clear my card before investing?']
    }),
    subscriptions: (): DemoAnswer => {
        const unused = HOUSEHOLD.subscriptions.filter(item => !item.used)
        const total = HOUSEHOLD.subscriptions.reduce((sum, item) => sum + item.amount, 0)
        const yearly = unused.reduce((sum, item) => sum + item.amount, 0) * 12

        return {
            paragraphs: [
                `You have ${HOUSEHOLD.subscriptions.length} subscriptions costing ${money(total)} a month. Two of them seem to sit idle:`,
                ...unused.map(item => `• ${item.label} at ${money(item.amount, 2)} a month, ${item.note}`),
                `Dropping both would free about ${money(yearly)} a year. Shall I nudge you before they next renew?`
            ],
            followUps: ['When will my savings hit $10,000?', 'Can you draw up a budget for me?']
        }
    },
    emergency: (): DemoAnswer => {
        const months = SAVINGS / ESSENTIALS
        const target = ESSENTIALS * 6
        const gapMonths = Math.ceil((target - SAVINGS) / 300)

        return {
            paragraphs: [
                `Your ${money(SAVINGS)} in savings would cover about ${months.toFixed(1)} months of essentials, which run ${money(ESSENTIALS)} a month.`,
                `Six months, roughly ${money(target)}, is a sound target. Setting aside ${money(300)} a month closes the gap in about ${formatDuration(gapMonths)}.`,
                'If your income feels very stable, four to five months is a sensible middle path.'
            ],
            followUps: ['Is there room to invest $500 this month?', 'Should I clear my card before investing?']
        }
    },
    retirement: (): DemoAnswer => {
        const months = (65 - HOUSEHOLD.age) * 12
        const projected = futureValue(41200, HOUSEHOLD.retirementMonthly, 0.07, months)
        const extra = futureValue(0, 150, 0.07, months)

        return {
            paragraphs: [
                'You are in good shape overall.',
                `With ${money(41200)} already set aside and ${money(HOUSEHOLD.retirementMonthly)} added each month, employer match included, you could reach about ${money(Math.round(projected / 10000) * 10000)} by 65 at an average of 7% growth a year.`,
                `That might fund around ${money(Math.round((projected * 0.04) / 12 / 50) * 50)} a month in retirement, before Social Security. Putting in ${money(150)} more each month would add roughly ${money(Math.round(extra / 10000) * 10000)}.`
            ],
            followUps: ['Am I paying more tax than I need to?', 'What size emergency fund do I need?']
        }
    },
    debt: (): DemoAnswer => ({
        paragraphs: [
            'Clear the credit card first.',
            `At 22.9% interest it costs more than investing is likely to earn, so paying off the ${money(1240)} balance is the surest return open to you. You could settle it this month from your ${money(LEFTOVER)} of free cash.`,
            'The 6.4% car loan is another story. Keep up the usual payments, and invest any extra once your emergency fund is in good order.'
        ],
        followUps: ['Is there room to invest $500 this month?', 'What size emergency fund do I need?']
    }),
    tax: (): DemoAnswer => ({
        paragraphs: [
            'A few easy wins to look into:',
            '• Your 401(k) contributions already reduce your taxable income, and raising them is often the simplest saving there is.',
            '• If your health plan is HSA-eligible, money you put in is tax-deductible and can be invested.',
            '• Losses in your investment account can balance out gains, and we can harvest them for you automatically.',
            'Julian Voss, one of our tax planning advisors, can check what suits you on a free first call.'
        ],
        followUps: ['Will I have enough when I retire?', 'Should I clear my card before investing?']
    }),
    home: (): DemoAnswer => {
        const tenPercent = 45000
        const monthly = 1000

        return {
            paragraphs: [
                `On a ${money(450000)} home, a 10% down payment comes to ${money(tenPercent)}, and 20% to ${money(90000)}.`,
                `Putting aside ${money(monthly)} a month gets you to 10% in about ${formatDuration(Math.ceil(tenPercent / monthly))}, with your emergency fund kept apart.`,
                'When you are nearly there, Sofia Navarro, our home buying advisor, can walk through mortgage options with you.'
            ],
            followUps: ['How soon could I reach $45,000?', 'Can you draw up a budget for me?']
        }
    },
    budget: (): DemoAnswer => {
        const needs = ESSENTIALS
        const wants = VARIABLE_TOTAL - 540 - 190
        const saving = HOUSEHOLD.autoSave

        return {
            paragraphs: [
                `Measured against the 50/30/20 guideline, here is how your ${money(HOUSEHOLD.income)} of take-home pay splits:`,
                `• Needs: ${money(needs)}, ${Math.round((needs / HOUSEHOLD.income) * 100)}% (guideline 50%), led by rent`,
                `• Wants: ${money(wants)}, ${Math.round((wants / HOUSEHOLD.income) * 100)}% (guideline 30%)`,
                `• Savings: ${money(saving)}, ${Math.round((saving / HOUSEHOLD.income) * 100)}% (guideline 20%)`,
                `There is space to save more. Lifting your automatic savings to ${money(1000)} takes you to 16% and still leaves ${money(LEFTOVER - 400)} free every month.`
            ],
            followUps: ['Where does my money actually go?', 'Am I paying for subscriptions I never use?']
        }
    },
    spending: (): DemoAnswer => {
        const sorted = [...HOUSEHOLD.variable].sort((a, b) => b.amount - a.amount).slice(0, 4)

        return {
            paragraphs: [
                `On top of ${money(FIXED_TOTAL)} in fixed bills, everyday spending has come to ${money(VARIABLE_TOTAL)} this month:`,
                ...sorted.map(item => `• ${item.label}: ${money(item.amount)}, ${Math.round((item.amount / VARIABLE_TOTAL) * 100)}%`),
                'Keep an eye on dining out. It is 22% above normal, mostly from weekday lunches.'
            ],
            followUps: ['What did eating out cost me this month?', 'Can you draw up a budget for me?']
        }
    },
    invest: (): DemoAnswer => ({
        paragraphs: [
            `Your low-cost index portfolio holds ${money(28900)} and has grown 9.4% over the past year.`,
            'Before adding to it, I would pay off the credit card, because its 22.9% interest outweighs what the market is likely to return.',
            `Once that is done, ${money(300)} a month could become roughly ${money(Math.round(futureValue(0, 300, 0.07, 120) / 1000) * 1000)} over ten years at 7% a year.`
        ],
        followUps: ['Is there room to invest $500 this month?', 'Should I clear my card before investing?']
    }),
    trip: (): DemoAnswer => {
        const { saved, target, monthly, label } = HOUSEHOLD.trip
        const months = Math.ceil((target - saved) / monthly)

        return {
            paragraphs: [
                `Your ${label.toLowerCase()} pot is at ${money(saved)} of its ${money(target)} target.`,
                `Adding ${money(monthly)} a month, you will be packed and ready in about ${formatDuration(months)}. Redirecting the ${money(62)} from your unused subscriptions would bring the date a little closer.`
            ],
            followUps: ['Am I paying for subscriptions I never use?', 'Is a $2,000 holiday within reach?']
        }
    },
    fallback: (): DemoAnswer => ({
        paragraphs: [
            'I did not quite catch that one.',
            `Here I can cover ${HOUSEHOLD.name}'s spending, budget, savings goals, debt, investing, retirement and taxes. Pick a suggestion below to begin.`
        ],
        followUps: ['Is there room to invest $500 this month?', 'Where does my money actually go?']
    })
}

/** Picks an answer for a free-text question. */
export const answerQuestion = (input: string): DemoAnswer => {
    const question = input.toLowerCase().replace(/[’']/g, '').trim()
    const amount = parseAmount(question)
    const short = question.split(/\s+/).length <= 4

    if (short && /^(hi|hello|hey|good (morning|afternoon|evening))\b/.test(question)) return ANSWERS.greeting()
    if (short && has(question, ['thank', 'cheers', 'great', 'perfect'])) return ANSWERS.thanks()
    if (has(question, ['who are you', 'what are you', 'what can you do', 'how do you work', 'what can i ask'])) return ANSWERS.capabilities()
    if (has(question, ['subscription', 'unused', 'membership'])) return ANSWERS.subscriptions()
    if (has(question, ['emergency', 'rainy day', 'safety net', 'cushion'])) return ANSWERS.emergency()
    if (has(question, ['retire', '401', 'pension', 'ira'])) return ANSWERS.retirement()
    if (has(question, ['tax'])) return ANSWERS.tax()
    if (has(question, ['credit card', 'debt', 'pay off', 'payoff', 'car loan', 'interest rate', 'my card'])) return ANSWERS.debt()
    if (has(question, ['house', 'home', 'mortgage', 'down payment', 'deposit', 'apartment'])) return amount && has(question, ['save', 'how long']) ? timeToSave(amount) : ANSWERS.home()
    if (has(question, ['afford', 'can i buy', 'can i spend', 'should i buy', 'can i invest'])) return amount ? afford(amount, question) : ANSWERS.budget()
    if (amount && question.includes('invest')) return afford(amount, question)
    if (has(question, ['budget', '50/30/20', 'spending plan'])) return ANSWERS.budget()

    const category = HOUSEHOLD.variable.find(item => has(question, item.keywords))

    if (category && has(question, ['spend', 'spent', 'spending', 'how much', 'cost'])) return categoryAnswer(category)
    if (has(question, ['where', 'spend', 'spent', 'spending', 'money going', 'breakdown', 'expenses'])) return ANSWERS.spending()
    if (has(question, ['trip', 'holiday', 'vacation', 'travel', 'portugal'])) return amount ? afford(amount, question) : ANSWERS.trip()
    if (has(question, ['save', 'saving', 'how long', 'reach'])) return amount ? timeToSave(amount) : ANSWERS.emergency()
    if (has(question, ['invest', 'stock', 'portfolio', 'index fund', 'etf'])) return ANSWERS.invest()
    if (category) return categoryAnswer(category)

    return ANSWERS.fallback()
}

export const SUGGESTED_QUESTIONS = [
    'Is there room to invest $500 this month?',
    'Where does my money actually go?',
    'When will my savings hit $10,000?',
    'Should I clear my card before investing?',
    'Am I paying for subscriptions I never use?',
    'Will I have enough when I retire?',
    'What size emergency fund do I need?',
    'Is a $2,000 holiday within reach?',
    'What did eating out cost me this month?',
    'Can you draw up a budget for me?'
]
